// lurik-frontend/src/presentation/pages/workspace/settings/security-section/components/PasswordConfirmation.tsx
import React, { useState } from 'react';
import styled from 'styled-components';

interface PasswordConfirmationProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (password: string) => void;
  action: 'ativar' | 'desativar';
  loading: boolean;
}

// ✅ STYLED COMPONENTS (sem quebrar lógica existente)
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 20px;
`;

const Modal = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 24px 0 24px;
  border-bottom: 1px solid #e1e5e9;
  padding-bottom: 20px;
`;

const ModalTitle = styled.h3`
  color: #2c3e50;
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 24px;
  color: #7f8c8d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.2s;
  
  &:hover {
    background-color: #f8f9fa;
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalBody = styled.div`
  padding: 24px;
`;

const Form = styled.form`
  margin-bottom: 20px;
`;

const FormGroup = styled.div`
  margin-bottom: 20px;
`;

const FormLabel = styled.label`
  display: block;
  color: #2c3e50;
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 8px;
`;

const FormInput = styled.input<{ hasError: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 2px solid ${props => props.hasError ? '#e74c3c' : '#e1e5e9'};
  border-radius: 8px;
  font-size: 16px;
  transition: border-color 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3498db;
  }
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.span`
  color: #e74c3c;
  font-size: 12px;
  margin-top: 6px;
  display: block;
`;

const FormActions = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  ${props => props.variant === 'primary' ? `
    background-color: #3498db;
    color: white;
    
    &:hover:not(:disabled) {
      background-color: #2980b9;
    }
  ` : `
    background-color: #f8f9fa;
    color: #2c3e50;
    border: 1px solid #e1e5e9;
    
    &:hover:not(:disabled) {
      background-color: #e9ecef;
    }
  `}
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ModalFooter = styled.div`
  padding: 0 24px 24px 24px;
`;

const WarningText = styled.p`
  color: #e67e22;
  font-size: 13px;
  text-align: center;
  margin: 0;
  padding: 16px;
  background-color: #fef9e7;
  border-radius: 8px;
  border: 1px solid #fdeaa7;
  
  strong {
    color: #d68910;
  }
`;

export const PasswordConfirmation: React.FC<PasswordConfirmationProps> = ({
  isOpen,
  onClose,
  onConfirm,
  action,
  loading
}) => {
  // ✅ LÓGICA SIMPLES - sem quebrar nada existente
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setError('Senha é obrigatória');
      return;
    }

    setError('');
    onConfirm(password);
  };

  const handleClose = () => {
    if (!loading) {
      setPassword('');
      setError('');
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <Overlay>
      <Modal>
        <ModalHeader>
          <ModalTitle>
            Confirmar {action === 'ativar' ? 'Ativação' : 'Desativação'} de 2FA
          </ModalTitle>
          <CloseButton
            onClick={handleClose}
            disabled={loading}
            type="button"
          >
            ×
          </CloseButton>
        </ModalHeader>

        <ModalBody>
          <Form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="password">
                Digite sua senha para confirmar
              </FormLabel>
              <FormInput
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Sua senha"
                hasError={!!error}
                disabled={loading}
                autoFocus
              />
              {error && <ErrorMessage>{error}</ErrorMessage>}
            </FormGroup>

            <FormActions>
              <Button
                type="button"
                variant="secondary"
                onClick={handleClose}
                disabled={loading}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading || !password.trim()}
              >
                {loading ? 'Confirmando...' : `Confirmar ${action}`}
              </Button>
            </FormActions>
          </Form>
        </ModalBody>

        <ModalFooter>
          <WarningText>
            ⚠️ Esta ação afetará <strong>TODOS</strong> os usuários da empresa
          </WarningText>
        </ModalFooter>
      </Modal>
    </Overlay>
  );
};